/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable implicit-arrow-linebreak */
// ** React Imports
import { useContext } from "react";
import { AbilityContext } from "@src/utility/context/Can";

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveVerticalNavMenuItemComponent = (item: any) => {
  if (item.header) return "VerticalNavMenuSectionHeader";
  if (item.children) return "VerticalNavMenuGroup";
  return "VerticalNavMenuLink";
};

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveHorizontalNavMenuItemComponent = (item: any) => {
  if (item.children) return "HorizontalNavMenuGroup";
  return "HorizontalNavMenuLink";
};

const CHILD_ROUTE_ACTIONS = new Set(["view", "edit", "details", "add"]);

/**
 * Whether a sidebar nav item should stay active on list + detail child routes.
 * e.g. `/apps/business/businesses/list` stays active on `/apps/business/businesses/view/123`
 * Sibling modules under the same prefix (e.g. `/apps/platform-configuration/notification-logs`)
 * must not activate the parent `/apps/platform-configuration/app-notifications` item.
 */
export const isMenuNavLinkActive = (
  navLink: string | undefined,
  pathname: string
): boolean => {
  if (!navLink || !pathname) return false;
  if (pathname === navLink) return true;

  const navParts = navLink.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);

  // User type folders: /apps/user/{type} ↔ /apps/user/{type}/view/:id
  if (
    navParts.length >= 3 &&
    navParts[0] === "apps" &&
    navParts[1] === "user"
  ) {
    const userSegment = navParts[2];
    if (
      pathParts.length >= 4 &&
      pathParts[0] === "apps" &&
      pathParts[1] === "user" &&
      pathParts[2] === userSegment &&
      pathParts[3] === "view"
    ) {
      return true;
    }
  }

  // List pages: match sibling detail/edit routes in the same app module.
  if (navParts.length >= 3 && navParts[navParts.length - 1] === "list") {
    const moduleParts = navParts.slice(0, -1);
    const sameModule =
      moduleParts.length <= pathParts.length &&
      moduleParts.every((part, index) => part === pathParts[index]);

    if (!sameModule) return false;

    const childSegment = pathParts[moduleParts.length];
    return Boolean(childSegment && CHILD_ROUTE_ACTIONS.has(childSegment));
  }

  // Nested child routes only for known actions (view/edit/add/details),
  // not for sibling modules that share the same path prefix.
  if (pathname.startsWith(`${navLink}/`)) {
    const childSegment = pathname.slice(navLink.length + 1).split("/")[0];
    return CHILD_ROUTE_ACTIONS.has(childSegment);
  }

  return false;
};

/**
 * Check if the given item has the given url
 * in one of its children
 *
 * @param item
 * @param currentUrl
 */
export const hasActiveChild = (item: any, currentUrl: any) => {
  const { children } = item;

  if (!children) {
    return false;
  }

  for (const child of children) {
    if (child.children) {
      if (hasActiveChild(child, currentUrl)) {
        return true;
      }
    }

    if (
      child?.navLink &&
      currentUrl &&
      isMenuNavLinkActive(child.navLink, currentUrl)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Check if this is a children
 * of the given item
 *
 * @param children
 * @param openGroup
 * @param currentActiveGroup
 */
export const removeChildren = (
  children: any,
  openGroup: any,
  currentActiveGroup: any
) => {
  children.forEach((child: any) => {
    if (!currentActiveGroup.includes(child.id)) {
      const index = openGroup.indexOf(child.id);
      if (index > -1) openGroup.splice(index, 1);
      if (child.children)
        removeChildren(child.children, openGroup, currentActiveGroup);
    }
  });
};

// ✅ Helper function - no hooks here
const checkForVisibleChild = (arr: any, ability: any) => {
  return arr.some((i: any) => {
    if (i.children) {
      return checkForVisibleChild(i.children, ability);
    } else {
      // If no action/resource defined, show the item
      if (!i.action || !i.resource) {
        return true;
      }
      return ability?.can(i.action, i.resource) || false;
    }
  });
};

// ✅ NEW: Pure function versions (no hooks)
export const canViewMenuGroupWithAbility = (item: any, ability: any) => {
  // console.log("🔍 Checking menu group:", item.title || item.header, {
  //   hasAction: !!item.action,
  //   hasResource: !!item.resource,
  //   hasChildren: !!item.children
  // });
  
  const hasAnyVisibleChild = item.children && checkForVisibleChild(item.children, ability)
  
  if (!(item.action && item.resource)) {
    // console.log("✅ Menu group allowed (no permission check):", item.title || item.header, hasAnyVisibleChild);
    return hasAnyVisibleChild
  }
  
  const canView = ability?.can(item.action, item.resource) && hasAnyVisibleChild;
  // console.log("🔐 Menu group permission result:", item.title || item.header, canView);
  return canView;
};

// ✅ NEW: Pure function version (no hooks)
export const canViewMenuItemWithAbility = (item: any, ability: any) => {
  // console.log("🔍 Checking menu item:", item.title, {
  //   hasAction: !!item.action,
  //   hasResource: !!item.resource,
  //   navLink: item.navLink
  // });
  
  // If no action or resource is defined, allow the item to be shown
  if (!item.action || !item.resource) {
    // console.log("✅ Menu item allowed (no permission check):", item.title);
    return true;
  }

  const canView = ability?.can(item.action, item.resource) || false;
  // console.log("🔐 Menu item permission result:", item.title, canView);
  return canView;
};

// ✅ EXISTING: Hook-based functions (preserved for backward compatibility)
export const canViewMenuGroup = (item: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ability = useContext(AbilityContext);
  return canViewMenuGroupWithAbility(item, ability);
};

export const canViewMenuItem = (item: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ability = useContext(AbilityContext);
  return canViewMenuItemWithAbility(item, ability);
};
